import React, { useEffect, useState } from 'react';
import { Modal } from './comps';
import styled from 'styled-components';
import { Poppins, Spacer } from 'src/common';
import MaterialInput from 'src/components/form/MaterialInput';
import Button from 'src/components/form/Button';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useMutation } from 'react-query';
import { requestAssistance } from 'src/api/other';
import { Guard } from './Guard';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { useAuth } from 'src/state/auth';
import { showSuccessToast, toastMessages } from 'src/components/Toast';

const StyledModal = styled(Modal)`
  width: 566px;
  min-height: 200px;

  .success {
    height: 246px;
    display: flex;
    align-items: center;
    text-align: center;
  }

  .error-container {
    position: absolute;
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    bottom: 20px;
    left: 0;
    width: 100%;
  }
`;

const StyledMaterialInput = styled(MaterialInput)`
  background-color: ${({ theme }) => theme.colors.cultured};
  input {
    padding-left: 8px;
  }
`;

const validationSchema = Yup.object().shape({
  text: Yup.string().min(3).required('Required'),
});

interface IRequestAssistanceModalProps {
  onClose: () => void;
  source?: 'support' | 'request_assistance';
}

export const RequestAssistanceModal: React.FC<IRequestAssistanceModalProps> = ({
  onClose,
  source = 'request_assistance',
}) => {
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [isCloseRequested, setIsCloseRequested] = useState(false);
  const { workspaceId } = useAuth();

  const [update, { isLoading }] = useMutation(requestAssistance, {
    onSuccess: () => {
      showSuccessToast(toastMessages.assistance.submitted);
      setDone(true);
      resetForm();
    },
    onError: (err: any) => setError(err?.message || 'Something went wrong'),
  });

  const { values, errors, handleSubmit, isValid, validateForm, touched, handleBlur, handleChange, dirty, resetForm } =
    useFormik({
      initialValues: { text: '' },
      validationSchema,
      onSubmit: async ({ text }) => update({ text, url: window.location.href, workspaceId, source }),
    });

  useEffect(() => {
    validateForm();
  }, []);

  const introText =
    source === 'support'
      ? `If you’re having difficulty using the platform or need some support in getting the most from it,
         please use this form to describe how we can help.`
      : `Get expert help with your assessment or security programme. If you need additional expertise or resources,
         we're on-hand to help you identify, quantify and analyse your cyber risk. Use this form to describe and
         request the assistance you need from the Cydea team.`;

  return (
    <Guard
      onCancel={() => setIsCloseRequested(false)}
      onClose={onClose}
      isOpen={isCloseRequested}
      isDirty={dirty}
      modalComponent={
        <StyledModal
          onRequestClose={() => setIsCloseRequested(true)}
          isOpen
          persistent={isLoading}
          data-cy="request-assistance-modal"
        >
          <Poppins className="m-title" px={28}>
            {source === 'support' ? 'Request Support' : 'Request Assistance'}
          </Poppins>
          <Spacer $px={8} />
          {done ? (
            <div className="success">
              <Poppins px={14} color="cflowerBlue">
                We have received your request and sent you a copy for your records. A member of the Cydea team will be
                in touch soon to discuss and get you the help you need.
              </Poppins>
            </div>
          ) : (
            <>
              <Poppins px={14} color="cflowerBlue">
                {introText}
              </Poppins>
              <Spacer $px={8} />
              <StyledMaterialInput
                name="text"
                placeholder="Your message"
                value={values.text}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.text && !!errors.text}
                multiline
                fullWidth
                rows={7}
              />
            </>
          )}
          <Spacer $px={30} />

          <Button
            css="width: 100%"
            primary
            onClick={() => {
              handleSubmit();
              mpEvent(MPEvents.ButtonClick, {
                button: 'Submit',
                modal: 'Request Assistance modal',
              });
            }}
            disabled={!isValid || isLoading || done}
            loading={isLoading}
          >
            SUBMIT
          </Button>

          {error && (
            <>
              <Spacer $px={10} />
              <div className="error-container">
                <Poppins px={14} color="error">
                  {error}
                </Poppins>
              </div>
            </>
          )}
        </StyledModal>
      }
    />
  );
};
